/**
 * The header bell (NB1, Daniel 2026-08-01).
 *
 * Built after looking at KW Command's, which read **801 unread**. That number
 * is the whole design brief. A bell that can never reach zero stops being
 * read, and then the one notification that mattered is indistinguishable from
 * the eight hundred that did not.
 *
 * Three choices follow from it:
 *
 *   1 · READING IS PASSIVE, DISMISSING IS DELIBERATE. Opening the panel marks
 *       what you saw as read. Removing a row is a separate click. Command
 *       conflates them, so the only way to clear the count is "Mark all as
 *       read", which also buries the two you were keeping.
 *
 *   2 · "CLEAR READ" NEVER TOUCHES UNREAD. You can empty the list without
 *       risking anything you have not looked at, which is the reason people
 *       actually press it.
 *
 *   3 · MARK UNREAD EXISTS. Glancing at your phone should not destroy your
 *       only reminder that a lead still needs a call.
 *
 * Polls every 30s. Not a websocket: the text and the email are the real-time
 * channels, this is the "find it again" surface, and half a minute of lag on
 * a badge is not worth a persistent connection.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  clearReadNotifications,
  dismissNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  type AppNotification,
} from '../api/notifications';
import { Icon } from './Icon';

const POLL_MS = 30_000;

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'unread' | 'read'>('unread');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', tab],
    queryFn: () => listNotifications(tab),
    refetchInterval: POLL_MS,
    // Keep polling with the panel shut — the badge is the point.
    refetchIntervalInBackground: false,
  });

  const items = data?.notifications ?? [];
  const unread = data?.unread_count ?? 0;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['notifications'] });

  const readOne = useMutation({ mutationFn: markNotificationRead, onSuccess: invalidate });
  const unreadOne = useMutation({ mutationFn: markNotificationUnread, onSuccess: invalidate });
  const dismissOne = useMutation({ mutationFn: dismissNotification, onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: markAllNotificationsRead, onSuccess: invalidate });
  const clearRead = useMutation({ mutationFn: clearReadNotifications, onSuccess: invalidate });

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Click outside closes. Ignores clicks inside the panel AND on the button,
  // or the button's own toggle would immediately reopen it.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const PANEL_W = 380;

  /**
   * Position the panel from the button's measured rect, in viewport
   * coordinates.
   *
   * First attempt used `position: absolute; right: 0` inside the sidebar and
   * it rendered mostly off the left edge of the screen — the rail is only
   * ~214px wide, so a 380px panel hung off it, and the rail's `overflow:
   * hidden` clipped what was left. Anchoring left-to-left and going out over
   * the main content is the only direction with room. `position: fixed`
   * escapes the clip; the rail sets no transform, so nothing traps it.
   */
  function measure() {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxLeft = window.innerWidth - PANEL_W - 12;
    setAnchor({
      top: r.bottom + 8,
      // Clamped both ways so it never leaves the viewport on a narrow
      // window, and never tucks under the rail on a wide one.
      left: Math.max(12, Math.min(r.left, maxLeft)),
    });
  }

  function openPanel() {
    if (!open) measure();
    setOpen((v) => !v);
  }

  // Keep it under the button while it's open. Without this it detaches and
  // floats over the page during a scroll or a window resize.
  useEffect(() => {
    if (!open) return;
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  /*
   * Close if the button itself leaves the screen.
   *
   * Daniel 2026-08-01: collapsing the left rail left the panel floating in
   * the middle of the page with nothing attached to it. The rail does not
   * unmount when collapsed, it slides out on `transform: translateX(-264px)`
   * and comes back on hover — so the button was still mounted, still had a
   * rect, and neither scroll nor resize fired. The panel had no idea.
   *
   * Watching the BUTTON's visibility rather than the sidebar's state is the
   * version that keeps working: the bell does not need to know what a
   * sidebar is, and this also covers the mobile drawer closing and anything
   * that hides it later.
   */
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (!entry.isIntersecting) setOpen(false); },
      // Any part off-screen is enough. The rail slides fully out, so a
      // stricter threshold would only add lag before it closes.
      { threshold: 0 },
    );
    io.observe(btnRef.current);
    return () => io.disconnect();
  }, [open]);

  function handleClick(n: AppNotification) {
    if (!n.read_at) readOne.mutate(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  }

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={openPanel}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        style={{
          position: 'relative',
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 8,
          // 44px on a phone is the tappable floor the rest of the app uses;
          // 36 is fine beside the desktop Create button, which is 31.
          minWidth: 40,
          minHeight: 36,
          padding: 0,
          flexShrink: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--chrome-ink)',
        }}
      >
        <Icon name="bell" size={17} />
        {unread > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              // --danger, not KW red. tokens.css rule 4: brand red is
              // logo-only so it cannot collide with urgency. I put it on an
              // unread count, which made a routine badge read as an
              // emergency. Fixed by the 2026-08-02 audit.
              background: 'var(--danger)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            {/* Capped display. Past a couple of dozen the exact number stops
                meaning anything, and a four-digit badge is how you get 801. */}
            {unread > 20 ? '20+' : unread}
          </span>
        )}
      </button>

      {open && anchor && (
        <div
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'fixed',
            top: anchor.top,
            left: anchor.left,
            width: `min(${PANEL_W}px, calc(100vw - 24px))`,
            maxHeight: 'min(70vh, 560px)',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-surface)',
            color: 'var(--text-heading)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
            zIndex: 300,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 14px', borderBottom: '1px solid var(--border)',
            }}
          >
            <strong style={{ fontSize: 15, flex: 1 }}>Notifications</strong>
            {(['unread', 'read'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  background: tab === t ? 'var(--bg-surface-2)' : 'transparent',
                  border: 'none', borderRadius: 6, padding: '4px 10px',
                  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                  fontWeight: tab === t ? 700 : 500, color: 'inherit',
                }}
              >
                {t === 'unread' ? 'All' : 'Read'}
              </button>
            ))}
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {isLoading && (
              <p style={{ padding: 18, fontSize: 13, color: 'var(--text-muted)' }}>
                Loading.
              </p>
            )}

            {!isLoading && items.length === 0 && (
              /* Distinguishing "nothing here" from "nothing matched this tab"
                 — the same rule as R7 elsewhere in the app. */
              <p style={{ padding: 18, fontSize: 13, color: 'var(--text-muted)' }}>
                {tab === 'read'
                  ? 'Nothing read yet.'
                  : 'You are all caught up. New leads show up here.'}
              </p>
            )}

            {items.map((n) => (
              <div
                key={n.id}
                style={{
                  display: 'flex', gap: 10, padding: '11px 14px',
                  borderBottom: '1px solid var(--border)',
                  background: n.read_at ? 'transparent' : 'var(--bg-surface-2)',
                }}
              >
                {!n.read_at && (
                  <span
                    aria-hidden
                    style={{
                      width: 7, height: 7, borderRadius: 4, marginTop: 6,
                      background: 'var(--danger)', flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button
                    type="button"
                    onClick={() => handleClick(n)}
                    style={{
                      background: 'none', border: 'none', padding: 0, margin: 0,
                      textAlign: 'left', cursor: n.link ? 'pointer' : 'default',
                      font: 'inherit', color: 'inherit', width: '100%',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{n.title}</div>
                    {n.body && (
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
                        {n.body}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {relativeTime(n.created_at)}
                    </div>
                  </button>
                  <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                    <MiniAction
                      label={n.read_at ? 'Mark unread' : 'Mark read'}
                      onClick={() =>
                        (n.read_at ? unreadOne : readOne).mutate(n.id)}
                    />
                    <MiniAction label="Dismiss" onClick={() => dismissOne.mutate(n.id)} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              display: 'flex', gap: 8, padding: '10px 14px',
              borderTop: '1px solid var(--border)',
            }}
          >
            <MiniAction
              label="Mark all read"
              disabled={unread === 0}
              onClick={() => readAll.mutate()}
            />
            <span style={{ flex: 1 }} />
            {/* Named for exactly what it does. "Clear all" would imply it
                takes the unread ones too, which is the behaviour that makes
                people stop pressing it. */}
            <MiniAction label="Clear read" onClick={() => clearRead.mutate()} />
          </div>
        </div>
      )}
    </div>
  );
}

function MiniAction({
  label, onClick, disabled,
}: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'none', border: 'none', padding: 0,
        fontSize: 12, fontFamily: 'inherit',
        color: disabled ? 'var(--text-muted)' : 'var(--brand-primary)',
        cursor: disabled ? 'default' : 'pointer',
        fontWeight: 600,
      }}
    >
      {label}
    </button>
  );
}

/** "4m ago". Absolute timestamps make you do arithmetic to answer the only
 *  question that matters here, which is whether this is still warm. */
function relativeTime(unixSeconds: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - unixSeconds);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString('en-US');
}
